import {
  Button,
  ChevronLeftIcon,
  DismissFilledIcon,
  Flex,
  SearchField,
  Typography /** Media query result is used within a hook that is already executed client-only; this is safe to use. */,
  // eslint-disable-next-line no-restricted-imports
  useMobileMediaQuery,
} from '@iheartjane/reefer';
import { pluralize } from '@iheartjane/utils';
import { useEffect } from 'react';
import {
  Form,
  useLoaderData,
  useNavigate,
  useSearchParams,
} from 'react-router';
import type {
  ClientLoaderFunctionArgs,
  LinksFunction,
  LoaderFunctionArgs,
  MetaFunction,
} from 'react-router';

import { MenuHeader } from '@jane/bloom/components/menuHeader/menuHeader';
import {
  FilterSelector,
  MobileFiltersModal,
} from '@jane/bloom/components/productListingPage/categoryAndFilterSelectors';
import { ProductGrid } from '@jane/bloom/components/productListingPage/productGrid';
import { useMemoizedRowInstance } from '@jane/bloom/hooks/dm/useMemoizedRowInstance';
import { BloomFilterProvider } from '@jane/bloom/hooks/filters';
import { useNavigateBack } from '@jane/bloom/hooks/useNavigateBack';
import { useUserSearchQuery } from '@jane/bloom/hooks/useUserSearchQuery';
import { enhanceWithFilterState } from '@jane/bloom/loaders/clientLoader/filterStateManagement';
import { recommendedSortResults } from '@jane/bloom/loaders/products/recommendedSortResults.server';
import { JANE_GOLD_CONFIG_CONTEXT } from '@jane/bloom/middlewares/janeGoldConfigContext.server';
import { canonicalTag } from '@jane/bloom/resources/meta/canonicalTag';
import { description } from '@jane/bloom/resources/meta/description';
import { getRootLoaderData } from '@jane/bloom/resources/meta/getRootLoaderData';
import { noindex } from '@jane/bloom/resources/meta/noindex';
import { parentMeta } from '@jane/bloom/resources/meta/parentMeta';
import { preloadImage } from '@jane/bloom/resources/meta/preloadImage';
import { title } from '@jane/bloom/resources/meta/title';
import { trackSearchedMenuProducts } from '@jane/bloom/services/tracking/track';
import type { RouteHandle } from '@jane/bloom/types/routeHandle';
import { initializeDMContext } from '@jane/bloom/utils/dm/dmContext.server';
import { SEARCH_TERM_QUERY_KEY } from '@jane/bloom/utils/queryParams/queryParams';

import stylesLink from './styles.css?url';
import desktopStylesLink from './styles.desktop.css?url';
import mobileStylesLink from './styles.mobile.css?url';

export const handle: RouteHandle = {
  hydrate: true,
};

export const links: LinksFunction = () => [
  { href: stylesLink, rel: 'stylesheet' },
  { href: desktopStylesLink, media: '(min-width: 767px)', rel: 'stylesheet' },
  { href: mobileStylesLink, media: '(max-width: 767px)', rel: 'stylesheet' },
];

export const meta: MetaFunction<typeof loader> = (args) => {
  const { menuUrl, pinnedStore } = getRootLoaderData(args);
  const data = args.loaderData;

  const aboveTheFoldImages = (data?.menuProductImgsAboveTheFold || [])
    .map((product) => product.imageSrcSet?.fallbackSrc)
    .filter((value, index, array) => array.indexOf(value) === index);

  return [
    title('Search Results', pinnedStore.name),
    description(),
    canonicalTag(args.location, menuUrl),
    noindex(),
    ...aboveTheFoldImages.map(preloadImage),
    ...parentMeta(args),
  ];
};

export const clientLoader = async (args: ClientLoaderFunctionArgs) => {
  const serverLoader = await args.serverLoader<typeof loader>();
  const { filterArgs, searchCriteria } = serverLoader;
  return {
    ...serverLoader,
    availableFilters: await enhanceWithFilterState({
      filterArgs,
      searchCriteria,
    }),
  };
};
clientLoader.hydrate = true;

export async function loader({ context, request }: LoaderFunctionArgs) {
  const dmContext = await initializeDMContext({ context, request });
  const { goldConfig } = context.get(JANE_GOLD_CONFIG_CONTEXT);
  const results = await recommendedSortResults({
    dmContext,
    goldConfig,
    request,
  });
  return results;
}

export default function SearchRoute() {
  const goBack = useNavigateBack();
  const { searchResultsQuery, setSearchModalQuery, setSearchResultsQuery } =
    useUserSearchQuery();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentSearchText = searchParams.get(SEARCH_TERM_QUERY_KEY);
  const isMobile = useMobileMediaQuery({});
  const {
    availableFilters,
    brandSpecials,
    category,
    customLanguage,
    enabledSpecialsForRow: enabledStoreSpecials,
    janeGold,
    menuProductImgsAboveTheFold,
    recommendedSortResults,
    searchCriteria,
    sort: { currentSort, sortOptions },
  } = useLoaderData<typeof loader>();
  const rowInstance = useMemoizedRowInstance(recommendedSortResults);
  const navigate = useNavigate();

  const submitSearch = (searchText: string) => {
    if (searchText) {
      setSearchParams((prev) => {
        prev.set(SEARCH_TERM_QUERY_KEY, searchText);
        return prev;
      });
    }
  };

  const clearSearch = () => {
    navigate('/featured');
  };

  useEffect(() => {
    setSearchResultsQuery(currentSearchText || '');
    setSearchModalQuery(
      !isMobile && currentSearchText ? currentSearchText : ''
    ); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    if (!rowInstance) return;
    trackSearchedMenuProducts({
      hits: rowInstance?.totalProducts,
      searchCriteria,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(searchCriteria), rowInstance?.totalProducts]);

  if (!rowInstance) {
    return <></>;
  }

  const totalProductCount = rowInstance.totalProducts;
  const hasResults = rowInstance.products.length > 0;

  return (
    <BloomFilterProvider
      availableFilters={availableFilters}
      searchCriteria={searchCriteria}
    >
      <MenuHeader />
      <div className="filter-selector-container">
        <div className="search-product-count--desktop">
          <Typography variant="header">
            {pluralize({ noun: 'result', number: totalProductCount })}{' '}
            <Typography as="span" variant="body">
              for “{currentSearchText}”
            </Typography>
          </Typography>
          {/** @todo use elements with appropriate role */}
          {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
          <div className="dismiss-icon-container" onClick={() => clearSearch()}>
            <DismissFilledIcon className="dismiss-icon" />
          </div>
        </div>
        <FilterSelector
          hasJaneGoldProducts={janeGold.hasJaneGoldProducts}
          availableProductsCount={totalProductCount}
          sortOptions={sortOptions}
          currentSort={currentSort}
          shouldShowGold={janeGold.shouldShowGold}
        />
      </div>
      <main className="main">
        <Form
          className="search-form"
          name="Search Form"
          onSubmit={() => submitSearch(searchResultsQuery)}
        >
          <Button.Icon
            label="Go back"
            icon={<ChevronLeftIcon />}
            onClick={() => goBack()}
            variant="minimal"
          />
          <SearchField
            className="search-field"
            name={SEARCH_TERM_QUERY_KEY}
            label="Search"
            disableMobileInputStyling
            onChange={(val) => setSearchResultsQuery(val)}
            value={searchResultsQuery}
          />
        </Form>

        <Flex alignItems="center" justifyContent="space-between">
          <div className="search-product-count">
            <Typography variant="header" maxLines={2}>
              {pluralize({ noun: 'result', number: totalProductCount })}{' '}
              <Typography as="span" variant="body">
                for “{currentSearchText}”
              </Typography>
            </Typography>

            <Button.Icon
              ariaLabel="Go back"
              icon={<DismissFilledIcon />}
              onClick={() => clearSearch()}
              variant="minimal"
            />
          </div>
          <MobileFiltersModal
            availableProductsCount={totalProductCount}
            sortOptions={sortOptions}
            currentSort={currentSort}
            shouldShowGold={janeGold.shouldShowGold}
            hasJaneGoldProducts={janeGold.hasJaneGoldProducts}
          />
        </Flex>

        {!hasResults && (
          <Flex justifyContent="center" alignItems="center" height="500px">
            <Typography color="inherit" variant="title-bold">
              No Products Available
            </Typography>
          </Flex>
        )}
        {hasResults && (
          <ProductGrid
            brandSpecials={brandSpecials}
            customLanguage={customLanguage}
            key={category}
            preloadedProductIds={menuProductImgsAboveTheFold.map(
              ({ id }) => id
            )}
            rowInstance={rowInstance}
            specials={enabledStoreSpecials}
            janeGold={janeGold}
          />
        )}
      </main>
    </BloomFilterProvider>
  );
}
